<template>
  <div class="fwr-root" :class="[themeClass, positionClass]">
    <!-- Layer 1: floating windows are teleported here -->
    <div ref="windowsLayer" class="fwr-windows-layer"/>

    <!-- Layer 2: minimized taskbar -->
    <div class="fwr-taskbar-layer">
      <transition-group tag="div" name="fwr-pill" class="fwr-taskbar">
        <div
            v-for="win in minimizedWindows"
            :key="win.id"
            class="fwr-pill"
            :data-title="win.title"
        >
          <span
              class="fwr-pill__label"
              :title="win.title"
              @click="win.restore()"
          >{{ win.title }}</span>
          <button
              class="fwr-pill__close"
              title="Yopish"
              @click.stop="win.close()"
          >
            <i class="fas fa-xmark"/>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import {minimizeRegistry, registerPortal, unregisterPortal} from '@/services/floatingWindowRegistryService'

export default {
  name: 'FloatingWindowRoot',

  props: {
    name: {
      type: String,
      required: true,
    },
    theme: {
      type: String,
      default: 'dark',
      validator: v => ['dark', 'light'].includes(v),
    },
    position: {
      type: String,
      default: 'absolute',
      validator: v => ['absolute', 'fixed'].includes(v),
    },
  },

  computed: {
    minimizedWindows() {
      return minimizeRegistry.windows
    },
    themeClass() {
      return this.theme === 'light' ? 'fw-light' : 'fw-dark'
    },
    positionClass() {
      return this.position === 'fixed' ? 'fwr-pos-fixed' : 'fwr-pos-absolute'
    },
  },

  mounted() {
    registerPortal(this.name, {
      windowsEl: this.$refs.windowsLayer,
    })
  },

  beforeDestroy() {
    unregisterPortal(this.name)
  },
}
</script>

<style scoped>
/* ─── Root: anchors absolute children, position mode controlled by prop ─────── */
.fwr-root {
  pointer-events: none;
  overflow: hidden;
  z-index: 9999;
}

/* Mode: absolute — fills the nearest positioned ancestor (default, unchanged) */
.fwr-pos-absolute {
  position: absolute;
  inset: 0;
}

/* Mode: fixed — pinned to the viewport at all times, ignores page scroll
   and is unaffected by any ancestor's position/overflow (unlike sticky).
   inset: 0 works exactly as it does in absolute mode, just viewport-relative. */
.fwr-pos-fixed {
  position: fixed;
  inset: 0;
}

/* ─── Windows layer ──────────────────────────────────────────────────────────── */
.fwr-windows-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* ─── Taskbar layer ──────────────────────────────────────────────────────────── */
.fwr-taskbar-layer {
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  pointer-events: none;
}

/* ─── Pill row ───────────────────────────────────────────────────────────────── */
.fwr-taskbar {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 6px;
  pointer-events: none;
}

/* ─── Individual pill ────────────────────────────────────────────────────────── */
.fwr-pill {
  pointer-events: all;
  flex: 1 1 0;
  min-width: 48px;
  max-width: 160px;

  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px 0 10px;
  height: 32px;

  background: var(--fw-pill-bg);
  border: 1px solid var(--fw-pill-border);
  border-radius: 7px;
  box-shadow: var(--fw-pill-shadow);
  color: var(--fw-pill-color);

  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;

  position: relative;
  transition: background 0.15s, border-color 0.15s, transform 0.15s, color 0.15s, box-shadow 0.15s;
}

.fwr-pill:hover {
  background: var(--fw-pill-bg-hover);
  border-color: var(--fw-pill-border-hover);
  color: var(--fw-pill-color-hover);
  box-shadow: var(--fw-pill-shadow-hover);
  transform: translateY(-3px);
}

.fwr-pill:active {
  transform: translateY(-1px);
}

/* ─── Pill label ─────────────────────────────────────────────────────────────── */
.fwr-pill__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  pointer-events: all;
  padding: 0 2px;
}

/* ─── Pill close button ──────────────────────────────────────────────────────── */
.fwr-pill__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--fw-pill-close-color);
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s, color 0.15s, transform 0.1s;
}

.fwr-pill__close:hover {
  background: rgba(255, 60, 60, 0.2);
  color: #ff4444;
}

.fwr-pill__close:active {
  transform: scale(0.88);
}

/* ─── CSS tooltip ────────────────────────────────────────────────────────────── */
.fwr-pill::after {
  content: attr(data-title);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  white-space: nowrap;
  background: var(--fw-tooltip-bg);
  color: var(--fw-body-color);
  border: 1px solid var(--fw-tooltip-border);
  font-size: 11px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 99999;
}

.fwr-pill:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.fwr-pill:has(.fwr-pill__close:hover)::after {
  opacity: 0;
}

/* ─── Pill transitions ───────────────────────────────────────────────────────── */
.fwr-pill-enter-active {
  transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fwr-pill-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
  position: absolute;
}

.fwr-pill-enter,
.fwr-pill-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.88);
}
</style>